<script setup lang="ts">
/**
 * ChatView — `/chat` route (Story 303 + 304).
 *
 * - Streaming on by default; toggle switches between `complete()` and
 *   `stream()`.
 * - Composer: multi-line textarea + Cmd/Ctrl+Enter shortcut + Send button.
 * - Reset button clears the visible history via the chat store.
 * - Token-usage footer shows the last turn + cumulative session totals.
 * - Errors render as a Pajamas danger banner without mutating the history.
 *
 * Story 304 polish:
 * - Stop button to abort mid-stream via AbortSignal.
 * - Time-to-first-token displayed in the metadata footer of the bubble.
 *
 * The chat provider is injectable so unit tests can drive deterministic
 * deltas without spinning up the Anthropic SDK.
 */
import { computed, nextTick, onUnmounted, ref } from 'vue';
import PageHeader from '../components/PageHeader.vue';
import Card from '../components/Card.vue';
import AppAlert from '../components/AppAlert.vue';
import MessageBubble from '../components/MessageBubble.vue';
import { createChatStore, type ChatStore } from '../services/chatStore';
import type { ChatProvider } from '../services/chatProvider';

const props = defineProps<{
  provider: ChatProvider;
  systemPrompt?: string;
}>();

const store: ChatStore = createChatStore(props.systemPrompt);
const composer = ref<string>('');
const streamingMode = ref<boolean>(true);
const isPending = ref<boolean>(false);
const errorMessage = ref<string | null>(null);
const ttfbMs = ref<number | null>(null);
const abortController = ref<AbortController | null>(null);

const canSend = computed(() => composer.value.trim().length > 0 && !isPending.value);
const canStop = computed(() => isPending.value && abortController.value !== null);

async function send(): Promise<void> {
  const text = composer.value.trim();
  if (!text || isPending.value) return;
  errorMessage.value = null;
  ttfbMs.value = null;
  store.appendUser(text);
  composer.value = '';
  isPending.value = true;
  const controller = new AbortController();
  abortController.value = controller;
  const startedAt = performance.now();
  try {
    const turn = streamingMode.value
      ? await props.provider.stream(
          {
            systemPrompt: props.systemPrompt,
            history: store.exportHistory(),
            signal: controller.signal,
          },
          (chunk) => {
            if (ttfbMs.value === null) {
              ttfbMs.value = Math.round(performance.now() - startedAt);
            }
            store.pushDelta(chunk.delta);
          },
        )
      : await props.provider.complete({
          systemPrompt: props.systemPrompt,
          history: store.exportHistory(),
          signal: controller.signal,
        });
    store.appendAssistant(turn.text, {
      inputTokens: turn.inputTokens,
      outputTokens: turn.outputTokens,
    });
  } catch (err) {
    const message = err instanceof Error && err.message ? err.message : 'Unknown error';
    errorMessage.value = message;
    // partial draft preserved as labelled stopped message
    if (store.streamingDraft.value.length > 0) {
      store.appendAssistant(`${store.streamingDraft.value} (stopped)`);
    }
  } finally {
    isPending.value = false;
    abortController.value = null;
    store.streamingDraft.value = '';
    await nextTick();
  }
}

function stop(): void {
  abortController.value?.abort();
}

function reset(): void {
  store.reset();
  errorMessage.value = null;
  ttfbMs.value = null;
}

function handleKeydown(event: KeyboardEvent): void {
  // Cmd/Ctrl+Enter sends; plain Enter inserts newline (default).
  if ((event.metaKey || event.ctrlKey) && event.key === 'Enter') {
    event.preventDefault();
    void send();
  }
}

onUnmounted(() => {
  abortController.value?.abort();
});
</script>

<template>
  <div class="cwc-chat">
    <PageHeader
      title="Chat"
      subtitle="Single- and multi-turn conversations powered by ClaudeProvider + streamMessages."
    >
      <template #actions>
        <button
          type="button"
          class="cwc-chat__reset"
          data-testid="reset-button"
          @click="reset"
        >
          Reset conversation
        </button>
      </template>
    </PageHeader>

    <div v-if="errorMessage" data-testid="error-banner">
      <AppAlert variant="danger" title="Network error">{{ errorMessage }}</AppAlert>
    </div>

    <Card title="Conversation">
      <div class="cwc-chat__messages" data-testid="messages">
        <template v-for="message in store.messages" :key="message.id">
          <MessageBubble
            :role="message.role"
            :content="message.content"
            :input-tokens="message.inputTokens"
            :output-tokens="message.outputTokens"
          />
        </template>
        <!--
          Story 304: the streaming bubble owns aria-live="polite" via
          <MessageBubble streaming> so screen readers announce deltas
          incrementally without re-reading the full content. The buffer
          is rendered with renderMarkdownSafely so half-open code fences
          do not flash partially-rendered <pre> blocks.
        -->
        <MessageBubble
          v-if="store.streamingDraft.value.length > 0"
          role="assistant"
          :content="store.streamingDraft.value"
          streaming
          data-testid="streaming-bubble"
        />
        <div v-if="store.messages.length === 0 && !store.streamingDraft.value" class="cwc-chat__empty">
          Send a message to start the conversation.
        </div>
      </div>

      <template #footer>
        <div class="cwc-chat__footer">
          <div class="cwc-chat__usage">
            <span data-testid="last-tokens">
              Last turn:
              in {{ store.lastInputTokens.value ?? '—' }},
              out {{ store.lastOutputTokens.value ?? '—' }}
            </span>
            <span data-testid="total-tokens">
              Session: in {{ store.totalInputTokens.value }}, out {{ store.totalOutputTokens.value }}
            </span>
            <span v-if="ttfbMs !== null" data-testid="ttfb-display">
              Time-to-first-token: {{ ttfbMs }} ms
            </span>
          </div>
        </div>
      </template>
    </Card>

    <form class="cwc-chat__composer" data-testid="composer" @submit.prevent="send">
      <label for="cwc-composer-text" class="cwc-chat__composer-label">Message</label>
      <textarea
        id="cwc-composer-text"
        v-model="composer"
        rows="3"
        placeholder="Type a message and press Cmd/Ctrl+Enter to send."
        data-testid="composer-input"
        :disabled="isPending"
        @keydown="handleKeydown"
      />
      <div class="cwc-chat__composer-actions">
        <label class="cwc-chat__streaming-toggle">
          <input
            v-model="streamingMode"
            type="checkbox"
            data-testid="streaming-toggle"
            :disabled="isPending"
          />
          Streaming
        </label>
        <button
          type="submit"
          class="cwc-chat__send"
          data-testid="send-button"
          :disabled="!canSend"
        >
          Send
        </button>
        <button
          v-if="canStop"
          type="button"
          class="cwc-chat__stop"
          data-testid="stop-button"
          @click="stop"
        >
          Stop
        </button>
      </div>
    </form>
  </div>
</template>

<style scoped>
.cwc-chat__messages {
  display: flex;
  flex-direction: column;
  gap: var(--gl-spacing-3);
  min-height: 200px;
  max-height: 60vh;
  overflow-y: auto;
}

.cwc-chat__empty {
  color: var(--gl-fg-subtle);
  font-style: italic;
  text-align: center;
  padding: var(--gl-spacing-6);
}

.cwc-chat__composer {
  display: flex;
  flex-direction: column;
  gap: var(--gl-spacing-2);
  margin-top: var(--gl-spacing-4);
  padding: var(--gl-spacing-4);
  background: var(--gl-bg-surface);
  border: var(--gl-border-width) solid var(--gl-border-default);
  border-radius: var(--gl-border-radius-lg);
}

.cwc-chat__composer-label {
  font-weight: var(--gl-font-weight-bold);
  font-size: var(--gl-font-size-sm);
}

.cwc-chat__composer textarea {
  appearance: none;
  background: var(--gl-bg-surface);
  color: var(--gl-fg-default);
  border: var(--gl-border-width) solid var(--gl-border-default);
  border-radius: var(--gl-border-radius-base);
  padding: var(--gl-spacing-2) var(--gl-spacing-3);
  font: inherit;
  font-family: var(--gl-font-family-sans);
  resize: vertical;
  width: 100%;
}

.cwc-chat__composer-actions {
  display: flex;
  gap: var(--gl-spacing-3);
  align-items: center;
}

.cwc-chat__streaming-toggle {
  display: inline-flex;
  align-items: center;
  gap: var(--gl-spacing-1);
  font-size: var(--gl-font-size-sm);
}

.cwc-chat__send,
.cwc-chat__stop,
.cwc-chat__reset {
  appearance: none;
  border-radius: var(--gl-border-radius-base);
  border: var(--gl-border-width) solid var(--gl-border-default);
  padding: var(--gl-spacing-2) var(--gl-spacing-4);
  font: inherit;
  cursor: pointer;
}

.cwc-chat__send {
  background: var(--gl-accent-primary);
  color: var(--gl-accent-on-primary);
  border-color: var(--gl-accent-primary);
}

.cwc-chat__send:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.cwc-chat__stop {
  background: var(--gl-status-danger-bg);
  color: var(--gl-status-danger-fg);
  border-color: var(--gl-status-danger-fg);
}

.cwc-chat__footer,
.cwc-chat__usage {
  display: flex;
  flex-wrap: wrap;
  gap: var(--gl-spacing-4);
  font-size: var(--gl-font-size-sm);
  color: var(--gl-fg-subtle);
}
</style>
